import { useEffect, useMemo, useState } from "react";
import Modal from "@/components/base/Modal";
import { Input } from "@/components/base/FormControls";
import { cn } from "@/utils/cn";
import { dispatchNotice, describeSupabaseError } from "@/store/shared/dataErrors";
import {
  apercuSuppressionCompte,
  supprimerCompteSuperAdmin,
  type ApercuSuppressionCompte,
} from "@/store/platform/superAdminData";

interface SupprimerCompteModalProps {
  open: boolean;
  onClose: () => void;
  compteId: string;
  nom: string;
  email: string;
  typeLabel: string;
  onDeleted: () => void;
}

const PERIMETRE_LABELS: { key: string; label: string; singular: string }[] = [
  { key: "immeubles", label: "Immeubles", singular: "immeuble" },
  { key: "biens", label: "Biens", singular: "bien" },
  { key: "locataires", label: "Locataires", singular: "locataire" },
  { key: "contrats", label: "Contrats", singular: "contrat" },
  { key: "paiements", label: "Paiements", singular: "paiement" },
  { key: "documents", label: "Documents", singular: "document" },
  { key: "depenses", label: "Dépenses", singular: "dépense" },
  { key: "maintenances", label: "Maintenances", singular: "maintenance" },
  { key: "abonnements", label: "Abonnements", singular: "abonnement" },
  { key: "employes", label: "Employés", singular: "employé" },
  { key: "proprietaires", label: "Propriétaires gérés", singular: "propriétaire" },
];

const MOT_CONFIRMATION = "SUPPRIMER";

export default function SupprimerCompteModal({
  open,
  onClose,
  compteId,
  nom,
  email,
  typeLabel,
  onDeleted,
}: SupprimerCompteModalProps) {
  const [apercu, setApercu] = useState<ApercuSuppressionCompte | null>(null);
  const [chargement, setChargement] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [mot, setMot] = useState("");
  const [emailConfirmation, setEmailConfirmation] = useState("");
  const [enCours, setEnCours] = useState(false);
  const [echec, setEchec] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let actif = true;
    setApercu(null);
    setErreur(null);
    setMot("");
    setEmailConfirmation("");
    setEchec(null);
    setChargement(true);
    apercuSuppressionCompte(compteId)
      .then((data) => {
        if (actif) setApercu(data);
      })
      .catch((caught) => {
        if (actif) setErreur(describeSupabaseError(caught) || "Impossible de charger le périmètre du compte.");
      })
      .finally(() => {
        if (actif) setChargement(false);
      });
    return () => {
      actif = false;
    };
  }, [open, compteId]);

  const emailCible = apercu?.compte.email ?? email;
  const motValide = mot.trim() === MOT_CONFIRMATION;
  const emailValide = emailConfirmation.trim().toLowerCase() === emailCible.trim().toLowerCase();
  const peutSupprimer = Boolean(apercu) && motValide && emailValide && !enCours && !chargement;

  const lignesPerimetre = useMemo(() => {
    if (!apercu) return [];
    return PERIMETRE_LABELS.map((item) => ({
      ...item,
      valeur: apercu.perimetre[item.key] ?? 0,
    }));
  }, [apercu]);

  const fermer = () => {
    if (enCours) return;
    onClose();
  };

  const confirmer = async () => {
    if (!peutSupprimer) return;
    setEnCours(true);
    setEchec(null);
    try {
      await supprimerCompteSuperAdmin(compteId);
      dispatchNotice({
        scope: "Super Admin",
        message: `Le compte « ${nom} » a été supprimé définitivement.`,
        tone: "success",
      });
      onDeleted();
      onClose();
    } catch (caught) {
      setEchec(
        describeSupabaseError(caught) ||
          "La suppression du compte a échoué. Aucune donnée n'a été supprimée."
      );
    } finally {
      setEnCours(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={fermer}
      size="lg"
      title="Supprimer définitivement ce compte ?"
      description="Cette action est irréversible. Le compte et les données qui lui sont exclusivement rattachées seront définitivement supprimés."
    >
      <div className="flex flex-col gap-5">
        <div className="flex items-start gap-3 rounded-lg border border-primary-200 bg-primary-50/70 p-3.5">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700">
            <i className="ri-error-warning-line text-lg" aria-hidden="true" />
          </span>
          <p className="pt-1 text-sm leading-relaxed text-foreground-700">
            La suppression est définitive et ne peut pas être annulée. Une trace sera conservée dans le journal d'audit.
          </p>
        </div>

        <section className="rounded-lg border border-background-200 bg-background-50 p-4">
          <h3 className="text-xs font-label font-semibold uppercase tracking-wide text-foreground-400">
            Compte ciblé
          </h3>
          <dl className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <dt className="text-xs text-foreground-500">Nom</dt>
              <dd className="text-sm font-label font-medium text-foreground-950">{apercu?.compte.nom ?? nom}</dd>
            </div>
            <div>
              <dt className="text-xs text-foreground-500">Email</dt>
              <dd className="truncate text-sm font-label font-medium text-foreground-950">{emailCible}</dd>
            </div>
            <div>
              <dt className="text-xs text-foreground-500">Type</dt>
              <dd className="text-sm font-label font-medium text-foreground-950">{typeLabel}</dd>
            </div>
            <div>
              <dt className="text-xs text-foreground-500">Statut</dt>
              <dd className="text-sm font-label font-medium text-foreground-950">{apercu?.compte.statut ?? "—"}</dd>
            </div>
            {apercu?.compte.formule ? (
              <div>
                <dt className="text-xs text-foreground-500">Formule</dt>
                <dd className="text-sm font-label font-medium text-foreground-950">{apercu.compte.formule}</dd>
              </div>
            ) : null}
          </dl>
        </section>

        <section className="rounded-lg border border-background-200 bg-background-50 p-4">
          <h3 className="text-xs font-label font-semibold uppercase tracking-wide text-foreground-400">
            Données associées détectées
          </h3>
          {chargement && (
            <div className="mt-3 flex items-center gap-2 text-sm text-foreground-600">
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-background-200 border-t-primary-500" />
              Analyse du périmètre…
            </div>
          )}
          {!chargement && erreur && (
            <p className="mt-3 flex items-center gap-2 text-sm text-primary-700">
              <i className="ri-error-warning-line text-base" aria-hidden="true" />
              {erreur}
            </p>
          )}
          {!chargement && !erreur && apercu && (
            <ul className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3">
              {lignesPerimetre.map((item) => (
                <li
                  key={item.key}
                  className="flex items-center justify-between rounded-md bg-background-100 px-3 py-2 text-sm"
                >
                  <span className="text-foreground-600">{item.label}</span>
                  <span className="font-label font-semibold text-foreground-950">{item.valeur}</span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="suppr-mot" className="text-sm font-label font-medium text-foreground-800">
              Tapez <span className="font-semibold text-primary-700">{MOT_CONFIRMATION}</span> pour confirmer
            </label>
            <Input
              id="suppr-mot"
              value={mot}
              onChange={(event) => setMot(event.target.value)}
              placeholder={MOT_CONFIRMATION}
              autoComplete="off"
              disabled={enCours}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label htmlFor="suppr-email" className="text-sm font-label font-medium text-foreground-800">
              Saisissez l'adresse email du compte à supprimer
            </label>
            <Input
              id="suppr-email"
              value={emailConfirmation}
              onChange={(event) => setEmailConfirmation(event.target.value)}
              placeholder={emailCible}
              autoComplete="off"
              disabled={enCours}
            />
          </div>
        </section>

        {echec && (
          <div className="flex items-start gap-3 rounded-lg border border-primary-200 bg-primary-50/70 p-3.5">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700">
              <i className="ri-error-warning-line text-base" aria-hidden="true" />
            </span>
            <div className="min-w-0">
              <p className="text-sm font-label font-medium text-primary-800">
                La suppression du compte a échoué. Aucune donnée n'a été supprimée.
              </p>
              <p className="mt-0.5 text-xs leading-relaxed text-foreground-600">{echec}</p>
            </div>
          </div>
        )}

        <div className="flex flex-col-reverse gap-2 border-t border-background-200 pt-4 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={fermer}
            disabled={enCours}
            className="inline-flex items-center justify-center whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-5 py-2.5 text-sm font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-50 cursor-pointer"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={confirmer}
            disabled={!peutSupprimer}
            className={cn(
              "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors cursor-pointer",
              peutSupprimer ? "bg-primary-600 hover:bg-primary-700" : "cursor-not-allowed bg-primary-300"
            )}
          >
            {enCours ? (
              <>
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-background-50/40 border-t-background-50" />
                Suppression en cours…
              </>
            ) : (
              <>
                <i className="ri-delete-bin-6-line text-base" aria-hidden="true" />
                Supprimer définitivement
              </>
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
}